import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Callout, Flex, Spinner, Suspense } from 'core/components';
import { OAuthAuthorizationService, OAuthRedirectRefused } from 'app/util/oauthAuthorization';
import { clearOAuthSsoReturn } from 'app/util/oauthSsoReturn';

interface Props {
  authorizationService?: OAuthAuthorizationService;
}

export default function OAuthAuthorization({ authorizationService }: Props) {
  const { search } = useLocation();
  const [service] = useState(() => authorizationService ?? new OAuthAuthorizationService());
  const [failed, setFailed] = useState(false);
  const [refusedHost, setRefusedHost] = useState<string>();

  useEffect(() => {
    let active = true;
    // The attempt has resumed, so a later SSO login must not return here.
    clearOAuthSsoReturn();
    setFailed(false);
    setRefusedHost(undefined);
    service
      .complete(search)
      .then((redirectUrl) => {
        if (active) {
          window.location.replace(redirectUrl);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setRefusedHost(error instanceof OAuthRedirectRefused ? error.redirectHost : undefined);
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [search, service]);

  return (
    <Flex justifyContent="center" alignItems="center" minHeight="240px" maxWidth="480px" width="100%" mx="auto" p={3}>
      <Suspense
        loading={!failed}
        fallbackThreshold={0}
        fallback={
          <Flex role="status" aria-label="Completing authorization">
            <Spinner size={32} />
          </Flex>
        }
      >
        {refusedHost ? (
          <Callout intent="danger" title="Application not allowed">
            Kentik does not allow authorization codes to be sent to <strong>{refusedHost}</strong>. Contact Kentik
            support to allow this application.
          </Callout>
        ) : (
          <Callout intent="danger" title="Authorization unavailable">
            Return to your client and start again.
          </Callout>
        )}
      </Suspense>
    </Flex>
  );
}
